import React, { useState, useCallback, useRef, useEffect } from 'react';
import { LoadScript } from '@react-google-maps/api';
import MapComponent from './components/MapComponent';
import LeadsPanel from './components/LeadsPanel';
import AddressSearch from './components/AddressSearch';
import CostTracker from './components/CostTracker';
import HotspotPanel from './components/HotspotPanel';
import PaywallModal from './components/PaywallModal';
import OnboardingGuide from './components/OnboardingGuide';
import UsageIndicator from './components/UsageIndicator';
import LoginScreen from './components/LoginScreen';
import FlyerGenerator from './components/FlyerGenerator';
import DocxFlyerGenerator from './components/DocxFlyerGenerator';
import RoofDetailsDialog from './components/RoofDetailsDialog';
import CRMLeadsPanel from './components/CRMLeadsPanel';
import PropertyModal from './components/PropertyModal';
import useApiCostTracker from './hooks/useApiCostTracker';
import useParcelData from './hooks/useParcelData';
import useUsageTracker from './hooks/useUsageTracker';
import usePropertyLookup from './hooks/usePropertyLookup';
import { useLeadsDatabase } from './hooks/useLeadsDatabase';

const libraries = ['places', 'drawing', 'geometry'];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';

// Get saved location from localStorage or use default
const getSavedLocation = () => {
  try {
    const saved = localStorage.getItem('roofleads_last_location');
    if (saved) {
      const { lat, lng, zoom } = JSON.parse(saved);
      if (lat && lng) return { lat, lng, zoom: zoom || 17 };
    }
  } catch (e) {
    console.warn('Failed to load saved location:', e);
  }
  // Default to 2456 Providence Ct, Greenwood, IN 46143
  return { lat: 39.5812733, lng: -86.1055412, zoom: 17 };
};

// Get cached session data (leads, selection bounds)
const getCachedSession = () => {
  try {
    const cached = localStorage.getItem('roofleads_session');
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 24 hours old
      if (data.timestamp && Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {
    console.warn('Failed to load cached session:', e);
  }
  return null;
};

// Save session data to localStorage
const saveCachedSession = (leads, selectionBounds, selectedIndices) => {
  try {
    const data = {
      leads,
      selectionBounds,
      selectedIndices: Array.from(selectedIndices),
      timestamp: Date.now()
    };
    localStorage.setItem('roofleads_session', JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save session:', e);
  }
};

function App() {
  const savedLocation = getSavedLocation();
  const cachedSession = getCachedSession();

  const [center, setCenter] = useState({ lat: savedLocation.lat, lng: savedLocation.lng });
  const [initialZoom] = useState(savedLocation.zoom);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leads, setLeads] = useState(cachedSession?.leads || []);
  const [selectedLeads, setSelectedLeads] = useState(() => new Set(cachedSession?.selectedIndices || []));
  const [selectionBounds, setSelectionBounds] = useState(cachedSession?.selectionBounds || null);
  const [isLoading, setIsLoading] = useState(false);
  const [drawingMode, setDrawingMode] = useState(null);
  const [toast, setToast] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('leadgen_onboarded');
  });
  const [currentStep, setCurrentStep] = useState(cachedSession?.leads?.length > 0 ? 3 : 1);
  const [isLocating, setIsLocating] = useState(false);
  const [showHotspots, setShowHotspots] = useState(false);
  const [showFlyers, setShowFlyers] = useState(false);
  const [showDocxFlyers, setShowDocxFlyers] = useState(false);
  const [roofDetailsLead, setRoofDetailsLead] = useState(null);
  const [showCRM, setShowCRM] = useState(false);
  const [propertyModalData, setPropertyModalData] = useState(null); // For property click modal
  const mapRef = useRef(null);

  // Toast helper - defined early so it can be used in effects below
  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Show toast if session was restored (runs once on mount)
  useEffect(() => {
    if (cachedSession?.leads?.length > 0) {
      showToast(`Restored ${cachedSession.leads.length} leads from last session`);
    }
  }, [cachedSession?.leads?.length, showToast]);

  // Usage tracking for demo limits
  const {
    usage,
    isLoading: authLoading,
    isAdmin,
    isPaid,
    authVerified,
    user,
    showPaywall,
    setShowPaywall,
    paymentLink,
    trackAction,
    canPerform,
    getRemaining,
    login,
    logout,
    signup
  } = useUsageTracker();

  // API cost tracking
  const {
    apiCalls,
    costs,
    totalCost,
    totalCalls,
    trackApiCall,
    COST_PER_REQUEST
  } = useApiCostTracker();

  // Parcel data for lead enrichment
  const {
    isLoading: parcelLoading,
    enrichLeads,
    getHotspots,
    loadParcelData
  } = useParcelData();

  // Property lookup API (RentCast via Cloudflare worker)
  // Pass user email for admin unlimited access
  const {
    enrichLeads: enrichLeadsWithAPI,
    isLookingUp,
    lookupProgress,
    dailyLimit,
    getDailyLimit,
    rewardAction,
    userId: lookupUserId
  } = usePropertyLookup(user?.email);

  // D1 Database for CRM
  const {
    addLeads: saveLeadsToDB,
    isSyncing: isSavingToDB,
    fetchLeads: fetchDbLeads,
    leads: dbLeads,
    checkExistingAddresses,
    getAddressKey
  } = useLeadsDatabase();

  // Preload parcel data on mount
  useEffect(() => {
    loadParcelData();
  }, [loadParcelData]);

  // Fetch existing leads from DB on mount (to show as markers)
  useEffect(() => {
    fetchDbLeads();
  }, [fetchDbLeads]);

  // Fetch daily lookup limit on mount and when user changes
  useEffect(() => {
    getDailyLimit();
  }, [getDailyLimit, user?.email]);

  // Load server preferences on mount (resume session across devices)
  useEffect(() => {
    const loadServerPreferences = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(
          `${PROPERTY_API_URL}/preferences?userId=${user.id}`,
          { headers: { 'X-API-Key': PROPERTY_API_KEY } }
        );
        const data = await response.json();

        if (data.success && data.preferences) {
          const prefs = data.preferences;
          // Restore map position if available
          if (prefs.lat && prefs.lng) {
            setCenter({ lat: prefs.lat, lng: prefs.lng });
            if (mapRef.current) {
              mapRef.current.panTo({ lat: prefs.lat, lng: prefs.lng });
              if (prefs.zoom) mapRef.current.setZoom(prefs.zoom);
            }
          }
          showToast('Session restored from last visit');
        }
      } catch (e) {
        console.warn('Failed to load server preferences:', e);
      }
    };

    loadServerPreferences();
  }, [user?.id, showToast]);

  // Save preferences to server when map moves
  const saveServerPreferences = useCallback(async () => {
    if (!user?.id || !mapRef.current) return;

    const center = mapRef.current.getCenter();
    const zoom = mapRef.current.getZoom();
    if (!center) return;

    try {
      await fetch(`${PROPERTY_API_URL}/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': PROPERTY_API_KEY
        },
        body: JSON.stringify({
          userId: user.id,
          lat: center.lat(),
          lng: center.lng(),
          zoom: zoom
        })
      });
    } catch (e) {
      console.warn('Failed to save preferences:', e);
    }
  }, [user?.id]);

  // Save session to localStorage when leads/selection changes
  useEffect(() => {
    if (leads.length > 0) {
      saveCachedSession(leads, selectionBounds, selectedLeads);
    }
  }, [leads, selectionBounds, selectedLeads]);

  // Save map location to localStorage when it changes
  const saveMapLocation = useCallback(() => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) {
        const location = {
          lat: center.lat(),
          lng: center.lng(),
          zoom: zoom
        };
        localStorage.setItem('roofleads_last_location', JSON.stringify(location));
        // Also save to server (debounced by map idle event)
        saveServerPreferences();
      }
    }
  }, [saveServerPreferences]);

  const handleLocateMe = useCallback(() => {
    if (navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCenter(userLocation);
          if (mapRef.current) {
            mapRef.current.panTo(userLocation);
            mapRef.current.setZoom(17);
          }
          setIsLocating(false);
          showToast('Centered on your location!');
        },
        (error) => {
          setIsLocating(false);
          showToast('Could not get your location');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, [showToast]);

  const handlePlaceSelect = useCallback(async (place) => {
    // Check usage limit for house lookups
    if (!canPerform('houseLookup')) {
      setShowPaywall(true);
      return;
    }

    if (place.geometry && place.geometry.location) {
      // Track the lookup
      const trackResult = await trackAction('houseLookup');
      if (!trackResult.allowed) {
        setShowPaywall(true);
        return;
      }

      const newCenter = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      setCenter(newCenter);

      // Zoom to show the house
      if (mapRef.current) {
        mapRef.current.panTo(newCenter);
        mapRef.current.setZoom(19); // Close zoom to see the house
      }

      // Extract address components
      const streetNumber = place.address_components?.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = place.address_components?.find(c => c.types.includes('route'))?.long_name || '';
      const city = place.address_components?.find(c => c.types.includes('locality'))?.long_name || '';
      const zip = place.address_components?.find(c => c.types.includes('postal_code'))?.short_name || '';

      // Create a lead for the searched address and select it
      const searchedAddress = streetNumber && route ? `${streetNumber} ${route}`.toUpperCase() : '';

      if (zip) {
        // Load hot leads from this ZIP
        try {
          const res = await fetch('/hotspots_full.json');
          const hotspots = await res.json();
          const hotspot = hotspots.find(h => h.zip === zip);

          if (hotspot && hotspot.addresses) {
            // Convert to lead format
            let leads = hotspot.addresses.map((addr, i) => ({
              streetAddress: addr.a,
              city: hotspot.city,
              state: 'IN',
              zip: hotspot.zip,
              ownerName: addr.o,
              assessedValue: addr.v,
              sqft: addr.s,
              ownerOccupied: addr.oo === 1,
              score: addr.sc,
              estRoofSqft: Math.round(addr.s * 1.4),
              estRoofCostLow: Math.round(addr.s * 1.4 * 4),
              estRoofCostHigh: Math.round(addr.s * 1.4 * 8),
              leadQuality: addr.sc >= 70 ? 'hot' : addr.sc >= 50 ? 'warm' : 'cool',
              parcelMatch: true,
              placeId: `search-${zip}-${i}`
            }));

            // Find the searched address in leads and move it to top
            const searchedIndex = leads.findIndex(l =>
              l.streetAddress.toUpperCase().includes(searchedAddress) ||
              searchedAddress.includes(l.streetAddress.toUpperCase())
            );

            if (searchedIndex > 0) {
              const [searchedLead] = leads.splice(searchedIndex, 1);
              searchedLead.lat = newCenter.lat;
              searchedLead.lng = newCenter.lng;
              leads.unshift(searchedLead);
              // Select and highlight the searched address
              setSelectedLead(searchedLead);
            } else if (searchedAddress) {
              // Address not in hotspot data, add it as first lead
              const searchedLead = {
                streetAddress: searchedAddress,
                city: city,
                state: 'IN',
                zip: zip,
                lat: newCenter.lat,
                lng: newCenter.lng,
                parcelMatch: false,
                placeId: `searched-${Date.now()}`
              };
              leads.unshift(searchedLead);
              setSelectedLead(searchedLead);
            }

            setLeads(leads);
            setSelectedLeads(new Set(leads.map((_, i) => i)));

            const hotCount = leads.filter(l => l.leadQuality === 'hot').length;
            const ownerOccCount = leads.filter(l => l.ownerOccupied).length;
            showToast(`🔥 Loaded ${leads.length} leads in ${zip}! ${hotCount} hot, ${ownerOccCount} owner-occupied`);
            setCurrentStep(3);
          } else {
            // No hotspot data, just show the searched address
            if (searchedAddress) {
              const searchedLead = {
                streetAddress: searchedAddress,
                city: city,
                state: 'IN',
                zip: zip,
                lat: newCenter.lat,
                lng: newCenter.lng,
                parcelMatch: false,
                placeId: `searched-${Date.now()}`
              };
              setLeads([searchedLead]);
              setSelectedLeads(new Set([0]));
              setSelectedLead(searchedLead);
            }
            showToast(`No pre-loaded data for ZIP ${zip}. Draw a selection to find more addresses.`);
            setCurrentStep(2);
          }
        } catch (err) {
          console.error('Failed to load hotspot data:', err);
          setCurrentStep(2);
        }
      } else {
        setCurrentStep(2);
      }
    }
  }, [showToast, canPerform, trackAction, setShowPaywall]);

  const handleLeadsFound = useCallback(async (newLeads) => {
    // Check usage limit for boundary draws
    if (!canPerform('boundaryDraw')) {
      setShowPaywall(true);
      return;
    }

    // Track the boundary draw
    const trackResult = await trackAction('boundaryDraw');
    if (!trackResult.allowed) {
      setShowPaywall(true);
      return;
    }

    // Track leads extracted
    if (newLeads.length > 0) {
      await trackAction('extractLeads', newLeads.length);
    }

    // Enrich leads with parcel data
    const enrichedLeads = await enrichLeads(newLeads);

    // Check which leads are already in DB
    const existingIndices = await checkExistingAddresses(enrichedLeads);

    // Create a set of DB address keys for fast lookup
    const dbAddressKeys = new Set(dbLeads.map(l => getAddressKey(l)));

    // Mark leads that are already in DB
    enrichedLeads.forEach((lead, index) => {
      const key = getAddressKey(lead);
      if (existingIndices.has(index) || dbAddressKeys.has(key)) {
        lead.inDatabase = true;
        // Check if it's also enriched in DB
        const dbLead = dbLeads.find(dl => getAddressKey(dl) === key);
        if (dbLead?.enriched) {
          lead.enriched = true;
          lead.inDatabase = true;
        }
      }
    });

    // Sort by score (hot leads first), with DB leads at the end
    enrichedLeads.sort((a, b) => {
      // DB leads go to the bottom
      if (a.inDatabase && !b.inDatabase) return 1;
      if (!a.inDatabase && b.inDatabase) return -1;
      // Then by score
      return (b.score || 0) - (a.score || 0);
    });

    setLeads(enrichedLeads);
    // Only select leads NOT in database
    const newLeadIndices = enrichedLeads
      .map((l, i) => l.inDatabase ? null : i)
      .filter(i => i !== null);
    setSelectedLeads(new Set(newLeadIndices));

    if (enrichedLeads.length > 0) {
      const hotCount = enrichedLeads.filter(l => l.leadQuality === 'hot').length;
      const ownerOccCount = enrichedLeads.filter(l => l.ownerOccupied).length;
      const dbCount = enrichedLeads.filter(l => l.inDatabase).length;
      const remaining = getRemaining('boundaryDraw');
      const dbMsg = dbCount > 0 ? ` | ⚡ ${dbCount} already in DB` : '';
      showToast(`Found ${enrichedLeads.length} addresses! 🔥 ${hotCount} hot, ${ownerOccCount} owner-occ${dbMsg} | ${remaining} draws left`);
      setCurrentStep(3);
    }
  }, [showToast, enrichLeads, canPerform, trackAction, setShowPaywall, getRemaining, checkExistingAddresses, dbLeads, getAddressKey]);

  // Handle hotspot blast - load all addresses from a ZIP
  const handleHotspotLoad = useCallback((leads, hotspot) => {
    // Debug: verify coordinates are generated
    const withCoords = leads.filter(l => l.lat && l.lng);
    console.log(`🔥 Hotspot: ${leads.length} leads, ${withCoords.length} with coords`);
    if (withCoords.length > 0) {
      console.log('Sample coords:', withCoords[0].lat?.toFixed(4), withCoords[0].lng?.toFixed(4));
    }
    console.log('Hotspot center:', hotspot.lat?.toFixed(4), hotspot.lng?.toFixed(4));

    setLeads(leads);
    // Don't auto-select all leads - let users see the map and select manually
    setSelectedLeads(new Set());
    setShowHotspots(false);

    // Pan map to the hotspot location
    if (hotspot.lat && hotspot.lng && mapRef.current) {
      const newCenter = { lat: hotspot.lat, lng: hotspot.lng };
      setCenter(newCenter);
      mapRef.current.panTo(newCenter);
      mapRef.current.setZoom(13); // Zoom out to show clusters first
    } else {
      console.warn('No hotspot coordinates available!');
    }

    const hotCount = leads.filter(l => l.leadQuality === 'hot').length;
    const ownerOccCount = leads.filter(l => l.ownerOccupied).length;
    showToast(`🔥 Loaded ${leads.length} addresses from ${hotspot.zip}! ${hotCount} hot, ${ownerOccCount} owner-occupied`);
    setCurrentStep(3);
  }, [showToast]);

  const toggleLeadSelection = useCallback((index) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const deleteLead = useCallback((index) => {
    setLeads(prev => {
      const newLeads = prev.filter((_, i) => i !== index);
      // Update selected leads indices
      setSelectedLeads(prevSelected => {
        const newSelected = new Set();
        prevSelected.forEach(i => {
          if (i < index) newSelected.add(i);
          else if (i > index) newSelected.add(i - 1);
        });
        return newSelected;
      });
      return newLeads;
    });
    showToast('Address removed');
  }, [showToast]);

  const handleLeadClick = useCallback((lead, index) => {
    console.log('=== Lead Click Debug ===');
    console.log('Lead:', lead.streetAddress, 'lat:', lead.lat, 'lng:', lead.lng);

    // On mobile, always show the property modal for full interaction
    const isMobile = window.innerWidth <= 640;
    if (isMobile) {
      // Show property modal on mobile for tagging, photos, etc.
      setPropertyModalData({
        address: `${lead.streetAddress}, ${lead.city}, ${lead.state} ${lead.zip}`,
        streetAddress: lead.streetAddress,
        city: lead.city,
        state: lead.state,
        zip: lead.zip,
        lat: lead.lat,
        lng: lead.lng,
        // Pass all lead data
        ownerName: lead.ownerName,
        assessedValue: lead.assessedValue,
        sqft: lead.sqft,
        yearBuilt: lead.yearBuilt,
        bedrooms: lead.bedrooms,
        bathrooms: lead.bathrooms,
        roofSqft: lead.roofSqft || lead.estRoofSqft,
        roofAge: lead.roofAge,
        roofPitch: lead.roofPitch,
        roofType: lead.roofType,
        roofCostAsphalt: lead.roofCostAsphalt || lead.estRoofCostLow,
        roofCostMetal: lead.roofCostMetal || lead.estRoofCostHigh,
        needsRoofReplacement: lead.needsRoofReplacement,
        roofSqftMeasured: lead.roofSqftMeasured,
        enriched: lead.enriched,
        ownerOccupied: lead.ownerOccupied,
        leadQuality: lead.leadQuality,
        tag: lead.tag,
        photo_url: lead.photo_url,
        dbLead: lead.inDatabase ? lead : null,
        isInDB: lead.inDatabase,
        sessionLeadIndex: index
      });
      return;
    }

    // On desktop, open roof details dialog if lead is enriched
    if (lead.enriched || lead.roofSqft || lead.roofCostAsphalt) {
      setRoofDetailsLead({ ...lead, index });
    }

    // If lead has real coordinates (not approximate), use them directly
    if (lead.lat && lead.lng && !lead.approximateLocation) {
      console.log('Using existing coordinates');
      setSelectedLead(lead);
      if (mapRef.current) {
        mapRef.current.panTo({ lat: lead.lat, lng: lead.lng });
        mapRef.current.setZoom(19);
      }
      return;
    }

    // Otherwise, geocode the address to get coordinates
    const address = `${lead.streetAddress}, ${lead.city}, ${lead.state} ${lead.zip}`;
    console.log('Geocoding address:', address);
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (trackApiCall) trackApiCall('geocoding');
      console.log('Geocode status:', status, 'results:', results?.length || 0);

      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const enrichedLead = {
          ...lead,
          lat: location.lat(),
          lng: location.lng(),
          approximateLocation: false // Now has real coordinates
        };

        console.log('Geocoded coordinates:', enrichedLead.lat, enrichedLead.lng);

        // Update the lead in the leads array with coordinates
        setLeads(prev => prev.map((l, i) => i === index ? enrichedLead : l));
        setSelectedLead(enrichedLead);

        if (mapRef.current) {
          mapRef.current.panTo({ lat: enrichedLead.lat, lng: enrichedLead.lng });
          mapRef.current.setZoom(19);
        }
      } else {
        // Still select even if geocode fails
        console.log('Geocode failed, selecting lead without coordinates');
        setSelectedLead(lead);
        showToast('Could not locate address on map');
      }
    });
  }, [trackApiCall, showToast]);

  const toggleSelectAll = useCallback(() => {
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map((_, i) => i)));
    }
  }, [selectedLeads.size, leads.length]);

  // Lookup detailed property data for selected leads
  const handleLookupDetails = useCallback(async () => {
    // BATCH LIMIT: Prevent accidentally looking up too many leads at once
    const MAX_BATCH_SIZE = 20; // Max leads per batch to prevent budget burnout

    // Get selected leads that need lookup (exclude already enriched OR in DB with enriched data)
    const selectedIndices = Array.from(selectedLeads);
    const leadsToLookup = selectedIndices
      .map(i => leads[i])
      .filter(lead => lead && !lead.enriched && !(lead.inDatabase && lead.enriched));

    // Count how many were skipped due to being in DB
    const skippedDbCount = selectedIndices
      .map(i => leads[i])
      .filter(lead => lead?.inDatabase && lead?.enriched).length;

    if (leadsToLookup.length === 0) {
      const msg = skippedDbCount > 0
        ? `All selected leads already looked up (${skippedDbCount} from DB)`
        : 'All selected leads already have detailed data';
      showToast(msg);
      return;
    }

    // BATCH LIMIT CHECK
    if (leadsToLookup.length > MAX_BATCH_SIZE) {
      const cost = (leadsToLookup.length * 0.06).toFixed(2);
      const confirmMsg = `⚠️ You're about to look up ${leadsToLookup.length} properties ($${cost} API cost).\n\nMax batch size is ${MAX_BATCH_SIZE} leads. Please select fewer leads or deselect some to continue.\n\nTip: Use "Select All" to toggle selection, then manually select just the leads you need.`;
      showToast(`Too many leads! Max ${MAX_BATCH_SIZE} per batch. Deselect some leads.`);
      alert(confirmMsg);
      return;
    }

    // Check usage limits (for non-admin/non-paid users)
    if (!isAdmin && !isPaid) {
      const remaining = getRemaining('propertyLookup');
      if (leadsToLookup.length > remaining) {
        showToast(`Only ${remaining} lookups remaining. Upgrade for unlimited.`);
        if (remaining === 0) {
          setShowPaywall(true);
          return;
        }
      }
    }

    // Show cost estimate for paid users
    const estimatedCost = (leadsToLookup.length * 0.06).toFixed(2);
    showToast(`Looking up ${leadsToLookup.length} properties (~$${estimatedCost})...`);

    try {
      // Enrich the selected leads
      const enrichedLeads = await enrichLeadsWithAPI(leadsToLookup, (current, total) => {
        // Progress callback - could update UI here
      });

      // Track the API usage
      if (!isAdmin && !isPaid) {
        const apiCallsMade = enrichedLeads.filter(l => l.enriched && !l.fromLocalCache).length;
        if (apiCallsMade > 0) {
          await trackAction('propertyLookup', apiCallsMade);
        }
      }

      // Merge enriched data back into leads array
      const updatedLeads = [...leads];
      let enrichedCount = 0;
      let cachedCount = 0;

      selectedIndices.forEach((leadIndex, i) => {
        const lead = leads[leadIndex];
        if (lead && !lead.enriched) {
          const enrichedLead = enrichedLeads.find(el =>
            el.streetAddress === lead.streetAddress ||
            (el.addressLine1 && lead.streetAddress.toUpperCase().includes(el.addressLine1.toUpperCase().split(' ')[0]))
          );
          if (enrichedLead?.enriched) {
            updatedLeads[leadIndex] = enrichedLead;
            enrichedCount++;
            if (enrichedLead.fromLocalCache || enrichedLead.cached) {
              cachedCount++;
            }
          }
        }
      });

      setLeads(updatedLeads);

      const newApiCalls = enrichedCount - cachedCount;
      if (newApiCalls > 0) {
        showToast(`✅ Enriched ${enrichedCount} leads (${cachedCount} cached, ${newApiCalls} API calls)`);
      } else {
        showToast(`✅ Enriched ${enrichedCount} leads (all from cache - $0 cost!)`);
      }

      // Auto-save enriched leads to CRM
      const enrichedToSave = updatedLeads.filter((l, i) => selectedIndices.includes(i) && l.enriched);
      if (enrichedToSave.length > 0) {
        try {
          await saveLeadsToDB(enrichedToSave);
          console.log(`Auto-saved ${enrichedToSave.length} enriched leads to CRM`);
        } catch (saveErr) {
          console.warn('Auto-save to CRM failed:', saveErr);
        }
      }
    } catch (e) {
      console.error('Lookup failed:', e);
      showToast('Failed to lookup some properties. Try again.');
    }
  }, [leads, selectedLeads, enrichLeadsWithAPI, showToast, isAdmin, isPaid, getRemaining, trackAction, setShowPaywall, saveLeadsToDB]);

  // Re-lookup a single lead (force refresh from API)
  const handleRelookup = useCallback(async (leadIndex) => {
    const lead = leads[leadIndex];
    if (!lead) return;

    showToast(`Re-looking up ${lead.streetAddress}...`);

    try {
      // Clear the cached flag so it fetches fresh from RentCast
      const address = `${lead.streetAddress}, ${lead.city}, ${lead.state} ${lead.zip}`;
      const response = await fetch(`${PROPERTY_API_URL}/lookup?address=${encodeURIComponent(address)}&force=true`, {
        headers: { 'X-API-Key': PROPERTY_API_KEY }
      });

      const data = await response.json();
      if (data.success) {
        // Update the lead with fresh data
        const updatedLead = {
          ...lead,
          enriched: true,
          ownerName: data.owner?.names?.join(', ') || lead.ownerName,
          sqft: data.property?.squareFootage || lead.sqft,
          yearBuilt: data.property?.yearBuilt || lead.yearBuilt,
          bedrooms: data.property?.bedrooms,
          bathrooms: data.property?.bathrooms,
          lotSize: data.property?.lotSize,
          assessedValue: data.valuation?.assessedValue || lead.assessedValue,
          roofSqft: data.roofEstimate?.roofSquareFootage,
          roofAge: data.roofEstimate?.roofAge,
          roofType: data.features?.roofType,
          needsRoofReplacement: data.roofEstimate?.needsReplacement,
          roofCostAsphalt: data.roofEstimate?.costEstimates?.asphalt?.estimate,
          roofCostMetal: data.roofEstimate?.costEstimates?.metal?.estimate,
          floorCount: data.features?.floorCount
        };

        setLeads(prev => prev.map((l, i) => i === leadIndex ? updatedLead : l));
        showToast(`Updated ${lead.streetAddress} with fresh data!`);
      } else {
        showToast(`Failed: ${data.error}`);
      }
    } catch (e) {
      console.error('Re-lookup failed:', e);
      showToast('Failed to re-lookup. Try again.');
    }
  }, [leads, showToast]);

  // Save selected leads to D1 database (CRM)
  const handleSaveToCRM = useCallback(async () => {
    const selectedIndices = Array.from(selectedLeads);
    const leadsToSave = selectedIndices.map(i => leads[i]).filter(Boolean);

    if (leadsToSave.length === 0) {
      showToast('Select at least one lead to save');
      return;
    }

    showToast(`Saving ${leadsToSave.length} leads to CRM...`);

    try {
      const result = await saveLeadsToDB(leadsToSave);
      if (result.success) {
        showToast(`✅ Saved ${result.inserted || leadsToSave.length} leads to CRM! (${result.duplicates || 0} duplicates skipped)`);
      } else {
        showToast(`Failed to save: ${result.error}`);
      }
    } catch (e) {
      console.error('Save to CRM failed:', e);
      showToast('Failed to save leads. Try again.');
    }
  }, [leads, selectedLeads, saveLeadsToDB, showToast]);

  const exportToCSV = useCallback(() => {
    const selectedAddresses = leads.filter((_, i) => selectedLeads.has(i));
    if (selectedAddresses.length === 0) {
      showToast('Select at least one address');
      return;
    }

    const headers = [
      'Address', 'City', 'State', 'ZIP',
      'Owner Name', 'Owner Occupied', 'Lead Score', 'Lead Quality',
      'Assessed Value', 'Est Roof Sqft', 'Est Roof Cost Low', 'Est Roof Cost High',
      'Home Sqft', 'Acreage',
      'Latitude', 'Longitude'
    ];
    const csvContent = [
      headers.join(','),
      ...selectedAddresses.map(lead => [
        `"${lead.streetAddress || ''}"`,
        `"${lead.city || ''}"`,
        `"${lead.state || ''}"`,
        `"${lead.zip || ''}"`,
        `"${(lead.ownerName || '').replace(/"/g, '""')}"`,
        lead.ownerOccupied ? 'Yes' : 'No',
        lead.score || '',
        lead.leadQuality || 'unknown',
        lead.assessedValue || '',
        lead.estRoofSqft || '',
        lead.estRoofCostLow || '',
        lead.estRoofCostHigh || '',
        lead.sqft || '',
        lead.acreage || '',
        lead.lat || '',
        lead.lng || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `roofing_leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    showToast(`Exported ${selectedAddresses.length} leads with owner data!`);
  }, [leads, selectedLeads, showToast]);

  const copyToClipboard = useCallback(() => {
    const selectedAddresses = leads.filter((_, i) => selectedLeads.has(i));
    if (selectedAddresses.length === 0) {
      showToast('Select at least one address');
      return;
    }

    const text = selectedAddresses.map(lead => lead.fullAddress).join('\n');
    navigator.clipboard.writeText(text);
    showToast(`Copied ${selectedAddresses.length} addresses!`);
  }, [leads, selectedLeads, showToast]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="app">
        <div className="loading-overlay" style={{ position: 'relative', minHeight: '100vh' }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔑</div>
            <h2 style={{ marginBottom: '1rem', color: '#f8fafc' }}>API Key Required</h2>
            <p style={{ color: '#94a3b8', maxWidth: '400px', lineHeight: 1.6, margin: '0 auto' }}>
              Create a <code style={{ background: '#334155', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>.env</code> file:
              <br /><br />
              <code style={{ background: '#334155', padding: '0.75rem 1rem', borderRadius: '8px', display: 'block' }}>
                REACT_APP_GOOGLE_MAPS_API_KEY=your_key
              </code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLocating || !center) {
    return (
      <div className="app">
        <div className="loading-overlay" style={{ position: 'relative', minHeight: '100vh' }}>
          <div className="spinner"></div>
          <div className="loading-text">Finding your location...</div>
        </div>
      </div>
    );
  }

  // Show loading while verifying auth with server
  if (authLoading) {
    return (
      <div className="app">
        <div className="loading-overlay" style={{ position: 'relative', minHeight: '100vh' }}>
          <div className="spinner"></div>
          <div className="loading-text">Verifying access...</div>
        </div>
      </div>
    );
  }

  // Require login before accessing the app (server verified)
  if (!isAdmin && !isPaid) {
    return <LoginScreen onLogin={login} onSignup={signup} isLoading={false} />;
  }

  return (
    <LoadScript
      googleMapsApiKey={GOOGLE_MAPS_API_KEY}
      libraries={libraries}
      loadingElement={
        <div className="loading-overlay" style={{ position: 'relative', minHeight: '100vh' }}>
          <div className="spinner"></div>
          <div className="loading-text">Loading maps...</div>
        </div>
      }
    >
      <div className="app">
        {/* Onboarding Guide */}
        <OnboardingGuide
          isVisible={showOnboarding}
          onClose={() => setShowOnboarding(false)}
        />

        {/* Paywall Modal */}
        <PaywallModal
          isVisible={showPaywall}
          onClose={() => setShowPaywall(false)}
          paymentLink={paymentLink}
          onLogin={login}
          usage={usage}
        />

        <header className="header">
          <div className="header-content">
            <div className="logo">
              <div className="logo-icon">
                <img src="/images/logo.webp" alt="RoofLeads Pro" />
              </div>
              <div>
                <div className="logo-text">RoofLeads Pro</div>
                <div className="logo-tagline">Lead Generation for Roofers</div>
              </div>
            </div>
            <AddressSearch onPlaceSelect={handlePlaceSelect} trackApiCall={trackApiCall} />
            <UsageIndicator
              usage={usage}
              isAdmin={isAdmin}
              isPaid={isPaid}
              onUpgrade={() => setShowPaywall(true)}
              onLogin={() => setShowPaywall(true)}
            />
            {(isAdmin || isPaid) && (
              <div className="user-menu">
                <span className="user-name">{user?.name || 'User'}</span>
                <button className="logout-btn" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <main className="main-content">
          <div className="map-section">
            <MapComponent
              center={center}
              initialZoom={initialZoom}
              drawingMode={drawingMode}
              setDrawingMode={setDrawingMode}
              onLeadsFound={handleLeadsFound}
              setIsLoading={setIsLoading}
              mapRef={mapRef}
              onLocateMe={handleLocateMe}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              trackApiCall={trackApiCall}
              selectedLead={selectedLead}
              onMapIdle={saveMapLocation}
              dbLeads={dbLeads}
              leads={leads}
              selectedLeadIndices={selectedLeads}
              onPropertyClick={(lead) => {
                // Show property modal when clicking a price marker (from DB)
                setPropertyModalData({
                  address: `${lead.streetAddress || lead.street_address}, ${lead.city}, ${lead.state} ${lead.zip || lead.zip_code}`,
                  streetAddress: lead.streetAddress || lead.street_address,
                  city: lead.city,
                  state: lead.state,
                  zip: lead.zip || lead.zip_code,
                  lat: lead.lat,
                  lng: lead.lng,
                  dbLead: lead,
                  isInDB: true
                });
              }}
              onLeadClick={(lead, index) => {
                // Handle click on session lead price marker
                handleLeadClick(index);
              }}
            />
            {isLoading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <div className="loading-text">Finding addresses...</div>
              </div>
            )}
          </div>

          <LeadsPanel
            leads={leads}
            selectedLeads={selectedLeads}
            selectedLead={selectedLead}
            onToggleSelection={toggleLeadSelection}
            onToggleSelectAll={toggleSelectAll}
            onLeadClick={handleLeadClick}
            onDeleteLead={deleteLead}
            onRelookup={handleRelookup}
            onExportCSV={exportToCSV}
            onCopyClipboard={copyToClipboard}
            onPrintFlyers={() => setShowFlyers(true)}
            onPrintDocxFlyers={() => setShowDocxFlyers(true)}
            onLookupDetails={handleLookupDetails}
            onSaveToCRM={handleSaveToCRM}
            isSavingToCRM={isSavingToDB}
            parcelLoading={parcelLoading}
            isLookingUp={isLookingUp}
            lookupProgress={lookupProgress}
            dailyLimit={dailyLimit}
          />
        </main>

        {/* Flyer Generator (PDF) */}
        <FlyerGenerator
          isVisible={showFlyers}
          onClose={() => setShowFlyers(false)}
          selectedLeads={selectedLeads}
          leads={leads}
        />

        {/* DOCX Flyer Generator */}
        <DocxFlyerGenerator
          isVisible={showDocxFlyers}
          onClose={() => setShowDocxFlyers(false)}
          selectedLeads={selectedLeads}
          leads={leads}
          onFlyerGenerated={async (count) => {
            // Reward user for generating flyers
            for (let i = 0; i < count; i++) {
              await rewardAction('flyer_generated');
            }
            showToast(`Earned ${count * 2} bonus lookups!`);
          }}
        />

        {/* Roof Details Dialog */}
        {roofDetailsLead && (
          <RoofDetailsDialog
            lead={roofDetailsLead}
            onClose={() => setRoofDetailsLead(null)}
            onRelookup={() => {
              if (roofDetailsLead.index !== undefined) {
                handleRelookup(roofDetailsLead.index);
              }
            }}
          />
        )}

        {toast && <div className="toast">{toast}</div>}

        {/* Hotspot Toggle Button */}
        {!showHotspots && (
          <button className="hotspot-toggle" onClick={() => setShowHotspots(true)}>
            🔥 Hot Regions
          </button>
        )}

        {/* CRM Toggle Button */}
        <button className="crm-toggle" onClick={() => setShowCRM(true)}>
          📋 CRM
        </button>

        {/* Hotspot Panel */}
        <HotspotPanel
          isVisible={showHotspots}
          onClose={() => setShowHotspots(false)}
          onLoadAddresses={handleHotspotLoad}
        />

        {/* CRM Leads Panel */}
        <CRMLeadsPanel
          isVisible={showCRM}
          onClose={() => setShowCRM(false)}
          userId={user?.id}
          onGenerateFlyer={(lead) => {
            // Convert CRM lead format to app lead format for flyer generation
            const appLead = {
              streetAddress: lead.street_address,
              city: lead.city,
              state: lead.state,
              zip: lead.zip_code,
              ownerName: lead.owner_name,
              assessedValue: lead.assessed_value,
              sqft: lead.sqft,
              yearBuilt: lead.year_built,
              roofSqft: lead.roof_sqft,
              roofAge: lead.roof_age,
              roofType: lead.roof_type,
              roofCostAsphalt: lead.roof_cost_low,
              roofCostMetal: lead.roof_cost_high,
              roofSqftMeasured: lead.roof_sqft_measured,
              roofPitch: lead.roof_pitch_degrees,
              bedrooms: lead.bedrooms,
              bathrooms: lead.bathrooms,
              enriched: lead.enriched
            };
            // Add to leads and show flyer generator
            setLeads([appLead]);
            setSelectedLeads(new Set([0]));
            setShowCRM(false);
            setShowDocxFlyers(true);
            showToast(`Generating flyer for ${lead.street_address}...`);
          }}
        />

        {/* Property Modal (from map click) */}
        {propertyModalData && (
          <PropertyModal
            property={propertyModalData}
            onClose={() => setPropertyModalData(null)}
            dbLead={propertyModalData.dbLead}
            isInDB={propertyModalData.isInDB}
            onAddToCRM={async (enrichedLead) => {
              const result = await saveLeadsToDB([enrichedLead]);
              if (result.success) {
                showToast('Lead saved to CRM!');
                fetchDbLeads();
              } else {
                showToast(`Failed: ${result.error}`);
              }
            }}
            onGenerateFlyer={(lead) => {
              setLeads([lead]);
              setSelectedLeads(new Set([0]));
              setPropertyModalData(null);
              setShowDocxFlyers(true);
            }}
          />
        )}

        {/* API Cost Tracker */}
        <CostTracker
          apiCalls={apiCalls}
          costs={costs}
          totalCost={totalCost}
          totalCalls={totalCalls}
          leadsCount={leads.length}
          COST_PER_REQUEST={COST_PER_REQUEST}
        />
      </div>
    </LoadScript>
  );
}

export default App;
